import React, { useEffect, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import type { PaymentMethod, PaymentRequest } from '@stripe/stripe-js';
import type { PremiumCheckoutPlanConfig } from './premiumCheckoutConfig';

type PremiumCheckoutApplePayButtonProps = {
  config: PremiumCheckoutPlanConfig;
  onPaymentMethodCreated: (pm: PaymentMethod) => void;
  /** When Apple Pay isn't available (e.g. local HTTP), still allow tapping to exercise the UI flow. */
  allowSimulateCheckout?: boolean;
  onSimulateCheckout?: () => void;
  disabled?: boolean;
  errorText?: string;
};

const PremiumCheckoutApplePayButton: React.FC<
  PremiumCheckoutApplePayButtonProps
> = ({
  config,
  onPaymentMethodCreated,
  allowSimulateCheckout = false,
  onSimulateCheckout,
  disabled = false,
  errorText = '',
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  );
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!stripe) return;

    let cancelled = false;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: config.applePayTotalLabel,
        amount: config.applePayAmountNow,
      },
      displayItems: config.applePayDisplayItems,
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.on('paymentmethod', (ev) => {
      onPaymentMethodCreated(ev.paymentMethod);
      ev.complete('success');
    });

    pr.canMakePayment()
      .then((result) => {
        if (cancelled) return;
        const available = Boolean(result?.applePay);
        setApplePayAvailable(available);
        if (available) {
          setPaymentRequest(pr);
        }
      })
      .finally(() => {
        if (!cancelled) setAvailabilityChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [stripe, config, onPaymentMethodCreated]);

  const canUseApplePay =
    applePayAvailable && Boolean(paymentRequest) && Boolean(stripe);
  const canSimulate =
    allowSimulateCheckout &&
    availabilityChecked &&
    !applePayAvailable &&
    Boolean(onSimulateCheckout);
  const canCheckout = canUseApplePay || canSimulate;

  const handleClick = async () => {
    setLocalError(null);

    if (canUseApplePay && paymentRequest) {
      try {
        paymentRequest.show();
      } catch (err) {
        console.error('Apple Pay show failed', err);
        setLocalError('Unable to open Apple Pay. Please try again.');
      }
      return;
    }

    if (canSimulate) {
      onSimulateCheckout?.();
      return;
    }
  };

  const displayError = errorText || localError;
  const isInsecureContext =
    typeof window !== 'undefined' && !window.isSecureContext;

  return (
    <div className="PremiumCheckoutApplePay">
      <button
        type="button"
        className="PremiumCheckoutApplePay__button"
        onClick={handleClick}
        disabled={disabled || !availabilityChecked || !canCheckout}
        aria-label="Check out with Apple Pay"
      >
        <span>Check Out with</span>
        <img
          className="PremiumCheckoutApplePay__logo"
          src={`${process.env.PUBLIC_URL}/brand/img-apple-pay-blk.svg`}
          alt=""
          aria-hidden
        />
      </button>

      {availabilityChecked && !applePayAvailable ? (
        <p className="PremiumCheckoutApplePay__hint">
          {isInsecureContext
            ? 'Apple Pay needs HTTPS (this page is HTTP). Tap the button to simulate checkout for UI testing.'
            : allowSimulateCheckout
              ? 'Apple Pay isn’t available here. Tap the button to simulate checkout for UI testing.'
              : 'Apple Pay isn’t available in this browser. Open this page from Safari on an Apple device (HTTPS) or from the Instant Heart Rate iOS app.'}
        </p>
      ) : null}

      {displayError ? (
        <div className="PremiumCheckoutApplePay__error" role="alert">
          {displayError}
        </div>
      ) : null}
    </div>
  );
};

export default PremiumCheckoutApplePayButton;
